import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import styled from 'styled-components';

import { MenuItemButton, MenuItemLink } from './shared/navigation.styles';
import { INavigationItem } from './shared/types';

import ChevronWrapper from '../../atoms/icons/ChevronWrapper';

import { DEQ_TYPES } from '../../../utils/deq';
import { getInternalLink } from '../../../utils/generic';

import { useNavigation } from './NavigationContextProvider';

interface Props {
  onItemClick: (activeItem?: INavigationItem) => void;
  activeTopItem?: INavigationItem;
}

const MobileNavigation = ({ onItemClick, activeTopItem }: Props) => {
  const data = useStaticQuery(graphql`
    query mobileNavigationMenu {
      mobileItems: contentfulMenuItem(name: { eq: "Mobile navigation" }) {
        subitems {
          displayName
          externalPage
          page {
            ... on ContentfulPageOverview {
              ...ParentPageOverview
            }
            ... on ContentfulPageGeneric {
              ...ParentPageGeneric
            }
            ... on ContentfulPageWizard {
              ...ParentPageWizard
            }
          }
          subitems {
            displayName
            externalPage
            page {
              ... on ContentfulPageOverview {
                ...ParentPageOverview
              }
              ... on ContentfulPageGeneric {
                ...ParentPageGeneric
              }
              ... on ContentfulPageWizard {
                ...ParentPageWizard
              }
            }
            subitems {
              displayName
              externalPage
              page {
                ... on ContentfulPageOverview {
                  ...ParentPageOverview
                }
                ... on ContentfulPageGeneric {
                  ...ParentPageGeneric
                }
                ... on ContentfulPageWizard {
                  ...ParentPageWizard
                }
              }
              subitems {
                displayName
                externalPage
                page {
                  ... on ContentfulPageOverview {
                    ...ParentPageOverview
                  }
                  ... on ContentfulPageGeneric {
                    ...ParentPageGeneric
                  }
                  ... on ContentfulPageWizard {
                    ...ParentPageWizard
                  }
                }
                subitems {
                  displayName
                  externalPage
                  page {
                    ... on ContentfulPageOverview {
                      ...ParentPageOverview
                    }
                    ... on ContentfulPageGeneric {
                      ...ParentPageGeneric
                    }
                    ... on ContentfulPageWizard {
                      ...ParentPageWizard
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `);

  const { setPosition } = useNavigation();

  const updatePosition = (index: number, newPosition: number) => {
    setPosition(index, newPosition);
  };

  return (
    <TopBar data-testid="mobile-navigation">
      {data.mobileItems.subitems.map((item: INavigationItem, key: number) => {
        return item.page ? (
          <MenuItemLink
            key={key}
            href={`/${getInternalLink(item.page)}`}
            onClick={() => updatePosition(0, key)}
            level={0}
            active={activeTopItem === item}
            data-testid="topbar-item-link"
            data-element-type={DEQ_TYPES.btnMenu}
            data-component-type={DEQ_TYPES.mainNavigation}
            data-element-label={item.displayName.toLowerCase()}
            data-element-hierarchy={item.displayName.toLowerCase()}
            data-element-position={key}
            data-element-target={`/${getInternalLink(item.page)}`}
            isMobile
          >
            {item.displayName}
          </MenuItemLink>
        ) : (
          <MenuItemButton
            key={key}
            onClick={() => {
              updatePosition(0, key);
              onItemClick(item);
            }}
            level={0}
            active={activeTopItem === item}
            data-testid="topbar-item-button"
            data-element-type={DEQ_TYPES.btnMenu}
            data-component-type={DEQ_TYPES.mainNavigation}
            data-element-label={item.displayName.toLowerCase()}
            data-element-hierarchy={item.displayName.toLowerCase()}
            data-element-position={key}
            isMobile
          >
            {item.displayName}
            <ChevronWrapper isActive={activeTopItem === item} />
          </MenuItemButton>
        );
      })}
    </TopBar>
  );
};

export default MobileNavigation;

const TopBar = styled.div`
  align-items: center;
  color: white;
  display: flex;
  opacity: 1;
  position: relative;
  scrollbar-width: none;
  transition: opacity 0.3s ease-in;
`;
